import { useTable, usePagination, type TableOptions, useSortBy } from 'react-table-next'
import { useMemo, useState } from 'react'
import { type TestableProps } from '../../../common/utils/testing/testing.types'
import {
  type DashTableData,
  type DashTableColumns,
  type DashTableDatum,
  type DashTableBaseContentProps,
  type DashTablePaginationConfigurationProps,
  type DashTableState,
  type DashTableLoadingErrorProps,
  type DashTableBaseFiltersType,
  type DashTableStyleOverrides,
  type DashTableManualPaginationProps,
} from './dashTable.types'
import { useDashTablePaginationConfiguration } from './dashTablePagination.utils'
import { useDashTableEffects } from './dashTableEffects.utils'

export type UseDashTableBehaviour<
  TData extends DashTableDatum,
  TFilters extends DashTableBaseFiltersType
> = DashTableManualPaginationProps<TFilters> &
  Pick<DashTablePaginationConfigurationProps, 'initialPageSize' | 'hidePageSizeSelector'> &
  Partial<DashTableLoadingErrorProps> &
  TestableProps &
  DashTableStyleOverrides & {
    data: DashTableData<TData>
    columns: DashTableColumns<TData>
    tableFooterPageDetails?: React.ReactNode
    emptyState?: React.ReactNode
  }

export const useDashTableBehaviour = <
  TData extends DashTableDatum,
  TFilters extends DashTableBaseFiltersType
>({
  loading = false,
  error,
  data,
  columns,
  manualPagination,
  fetchData,
  allowUnknownRecordCount,
  hidePageSizeSelector,
  totalRecordCount: inputRecordCount,
  initialPageSize = 25,
  filters,
}: UseDashTableBehaviour<TData, TFilters>) => {
  // Clear default min/max width settings applied by react table
  // Actual defaults should be applied in the HeaderCell file
  const defaultColumn = useMemo(
    () => ({
      minWidth: undefined,
      width: undefined,
      maxWidth: undefined,
    }),
    []
  )

  // In manual mode, we determine page count using the manualTotalRecordCount
  // First render is always 0
  const [pageCount, setPageCount] = useState<number>(0)

  const hiddenColumns = columns.filter(({ isVisible }) => isVisible === false).map(({ id }) => id)
  const initialState: DashTableState = {
    pageIndex: 0,
    pageSize: initialPageSize,
    hiddenColumns,
    sortBy: [],
  }

  const behaviour = useTable<TData>(
    {
      data,
      columns,
      defaultColumn,
      initialState,
      manualPagination,
      pageCount,
      manualSortBy: manualPagination,
      disableMultiSort: true,
      // React Table doesn't seem to recognize the manualPagination prop
    } as unknown as TableOptions<TData>,
    useSortBy,
    usePagination
    // React Table's plugin system doesn't play nice with TypeScript
    // Their suggested workaround: https://github.com/tannerlinsley/react-table/commit/cee24c74d847cdb7d9ea374af065e85e73138743?short_path=357a56b#diff-357a56bc8df6226426455af0a807d77b
    // This is a slightly altered version of their suggestion [Rather than overwriting the gobal type, we just override this instance]
  ) as unknown as DashTableBaseContentProps<TData>

  const paginationConfiguration = useDashTablePaginationConfiguration<TFilters>({
    manualPagination,
    hidePageSizeSelector,
    inputRecordCount,
    initialPageSize,
    state: behaviour.state,
    dataLength: data.length,
  })

  useDashTableEffects({
    setPageCount,
    fetchData,
    allowUnknownRecordCount,
    filters,
    manualPagination,
    paginationConfiguration,
    state: behaviour.state,
    dispatch: behaviour.dispatch,
  })

  return {
    loading,
    error,
    ...paginationConfiguration,
    ...behaviour,
  }
}
